/* Bundelkiezer (USA-thema, Horizon). Zelfde kiezer als NL (assets/mv-bundels.js daar, vervangt Kaching Bundles).
   - Kiest een bundel en per stuk een variant; de gewone variantkiezer van het thema is verborgen.
   - Bij "Add to cart" (submit in de capture-fase op window, dus vóór Horizon's eigen afhandeling op document)
     voegt dit script zelf toe via /cart/add.js: per variant één regel met _mv_bundel = <bar>:<uniek>, plus het
     cadeau met _mv_cadeau = dezelfde waarde. Daarna het CartLinesUpdateEvent van Horizon met de nieuwe
     winkelwagen en de secties van de winkelwagenlade, zodat lade, teller en regels bijwerken en de lade opengaat.
     (Horizon's eigen formulier gooit een fout zonder veld "id", daarom niet via items[] in dat formulier.)
   - Mix & Match (badpakken): een stuk na het eerste kan weg (kruisje) en via "Choose" uit het venster vervangen
     worden door een ander product uit de deal. Lege stuks gaan niet mee.
   - De korting rekent de kortingsfunctie uit; dit script rekent niets. */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

(() => {
  if (customElements.get('mv-bundels')) return;

  const ONBESCHIKBAAR = 'Sorry, this is currently unavailable.';
  const NIET_BESCHIKBAAR = ' - Unavailable'; // achter een optie waarvan die combinatie op is, zoals Kaching

  class MvBundels extends HTMLElement {
    connectedCallback() {
      this.varianten = JSON.parse(this.querySelector('[data-mvb-varianten]')?.textContent || '{}');
      this.fout = this.querySelector('.mvb__fout');
      this.venster = this.querySelector('[data-mvb-venster]');
      this.doelStuk = null;
      this.form = this.zoekFormulier();
      this.addEventListener('change', (e) => {
        if (e.target.matches('.mvb__invoer')) {
          const bar = e.target.closest('.mvb__bar');
          this.kies(bar);
          this.volgThema(bar?.querySelector('[data-stuk]'));
        }
        const stuk = e.target.matches('.mvb__keuze') && e.target.closest('[data-stuk], .mvb__keus');
        if (stuk) this.markeer(stuk);
        if (stuk && stuk.matches('[data-stuk]')) { this.controleer(stuk); this.toonBeeld(stuk); this.volgThema(stuk); }
      });
      this.addEventListener('click', (e) => {
        const maat = e.target.closest('[data-mvb-maattabel]');
        if (maat) { e.preventDefault(); this.openMaattabel(); return; }
        // knoppen staan in het <label> van de bar: zonder preventDefault zou de klik ook de radio aanzetten
        const weg = e.target.closest('[data-mvb-weg]');
        if (weg) { e.preventDefault(); this.leegStuk(weg.closest('[data-stuk]')); return; }
        const kies = e.target.closest('[data-mvb-kies]');
        if (kies) { e.preventDefault(); this.openVenster(kies.closest('.mvb__product')); return; }
        const neem = e.target.closest('[data-mvb-neem]');
        if (neem) { this.neem(neem.closest('.mvb__keus')); return; }
        if (e.target.closest('[data-mvb-sluit]') || e.target === this.venster) this.venster.close();
      });
      this.addEventListener('keydown', (e) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target.matches('[data-mvb-maattabel]')) { e.preventDefault(); this.openMaattabel(); }
      });
      this.opVersturen = (e) => { if (this.form && e.target === this.form) this.voorVersturen(e); };
      if (this.form) window.addEventListener('submit', this.opVersturen, { capture: true });
      else console.error('[mv-bundels] productformulier niet gevonden; bundels worden als los product toegevoegd');
      this.querySelectorAll('[data-stuk]').forEach((s) => { this.markeer(s); this.controleer(s); });
      this.querySelectorAll('.mvb__keus').forEach((k) => this.markeer(k));
    }

    disconnectedCallback() {
      if (this.opVersturen) window.removeEventListener('submit', this.opVersturen, { capture: true });
    }

    // het formulier met de koopknop in dezelfde productsectie (de kiezer staat zelf buiten dat formulier)
    zoekFormulier() {
      const sectie = this.closest('.shopify-section') || document;
      return sectie.querySelector('product-form-component form') || sectie.querySelector('form[action*="/cart/add"]');
    }

    gekozenBar() { return this.querySelector('.mvb__bar.is-gekozen'); }

    // Zoals Kaching in NL: de keuze in de bundel stuurt de (verborgen) variantkiezer van het thema mee. Dan
    // volgen knop ("Sold out" bij een uitverkochte maat), prijs, URL en het back-in-stock-blok de gekozen
    // maat. Gemeten 23 okt 2026: zonder dit bleef de knop "Add to cart" op een uitverkochte maat, NL niet.
    // Per optie één wissel, en wachten tot Horizon die verwerkt heeft: de opties van de volgende keuze
    // dragen de variant-id van de vorige.
    volgThema(stuk) {
      if (!stuk || (stuk.dataset.handle && stuk.dataset.handle !== this.dataset.handle)) return;
      const waarden = [...stuk.querySelectorAll('.mvb__keuze')].map((s) => s.value);
      this.themaRij = (this.themaRij || Promise.resolve()).then(() => this.zetThema(waarden)).catch((fout) => {
        console.warn('[mv-bundels] themakiezer niet bijgewerkt', fout);
      });
    }

    async zetThema(waarden) {
      const sectie = this.closest('.shopify-section') || document;
      for (let i = 0; i < waarden.length; i++) {
        const kiezer = sectie.querySelector('variant-picker');
        if (!kiezer) return;
        const opties = [...kiezer.querySelectorAll(`[data-input-id^="${i + 1}-"]`)];
        const doel = opties.find((o) => o.value === waarden[i]);
        if (!doel) continue; // optie met één waarde: Horizon toont daar geen kiezer
        let veld = doel;
        if (doel.tagName === 'OPTION') {
          veld = doel.closest('select');
          if (!veld || veld.value === doel.value) continue;
          veld.value = doel.value;
        } else {
          if (doel.checked) continue;
          doel.checked = true;
        }
        const verwerkt = new Promise((klaar) => {
          const opKeuze = (e) => { klaar(e.promise?.catch(() => null)); };
          sectie.addEventListener(StandardEvents.productSelect, opKeuze, { once: true });
          setTimeout(() => { sectie.removeEventListener(StandardEvents.productSelect, opKeuze); klaar(null); }, 4000);
        });
        veld.dispatchEvent(new Event('change', { bubbles: true }));
        await verwerkt;
      }
    }

    kies(bar) {
      this.querySelectorAll('.mvb__bar').forEach((b) => {
        const aan = b === bar;
        b.classList.toggle('is-gekozen', aan);
        const v = b.querySelector('.mvb__varianten');
        if (v) v.hidden = !aan;
      });
      this.toonFout('');
    }

    lijst(handle) { return this.varianten[handle || this.dataset.handle] || []; }

    variantVan(stuk) {
      const opties = [...stuk.querySelectorAll('.mvb__keuze')].map((s) => s.value);
      return this.lijst(stuk.dataset.handle).find((v) => v.opties.every((o, i) => o === opties[i]));
    }

    // zoals Kaching: alleen in het laatste vakje (de maat) " - Niet beschikbaar" achter elke waarde die met de
    // gekozen kleur geen leverbare variant geeft
    markeer(stuk) {
      const keuzes = [...stuk.querySelectorAll('.mvb__keuze')];
      const lijst = this.lijst(stuk.dataset.handle);
      const i = keuzes.length - 1;
      if (i >= 0) [...keuzes[i].options].forEach((o) => {
        const opties = keuzes.map((k, j) => (j === i ? o.value : k.value));
        const v = lijst.find((x) => x.opties.every((w, j) => w === opties[j]));
        o.textContent = o.value + (v && v.beschikbaar ? '' : NIET_BESCHIKBAAR);
      });
    }

    // Mix & Match: de foto van het stuk volgt de gekozen kleur, zoals Kaching
    toonBeeld(stuk) {
      const img = stuk.querySelector('[data-mvb-beeld]');
      const url = img && this.variantVan(stuk)?.beeld;
      if (url) { img.removeAttribute('srcset'); img.src = url; }
    }

    controleer(stuk) {
      const v = this.variantVan(stuk);
      const goed = !!(v && v.beschikbaar);
      stuk.querySelectorAll('.mvb__keuze').forEach((s) => s.classList.toggle('is-ongeldig', !goed));
      return goed;
    }

    toonFout(tekst) {
      this.fout.textContent = tekst;
      this.fout.hidden = !tekst;
    }

    leegStuk(stuk) {
      const leeg = this.querySelector('template[data-mvb-leeg]')?.content.firstElementChild;
      if (stuk && leeg) stuk.replaceWith(leeg.cloneNode(true));
    }

    openVenster(leeg) {
      if (!this.venster || !leeg) return;
      this.doelStuk = leeg;
      this.venster.showModal();
    }

    // het gekozen product uit het venster komt op de plek van het lege stuk, met de kleur/maat uit het venster
    neem(keus) {
      const sjabloon = keus?.querySelector('template[data-mvb-stuk]')?.content.firstElementChild;
      if (!sjabloon || !this.doelStuk?.isConnected) return;
      const v = this.variantVan(keus); // .mvb__keus heeft zelf data-handle en de keuzevakjes
      if (!v || !v.beschikbaar) {
        keus.querySelectorAll('.mvb__keuze').forEach((s) => s.classList.add('is-ongeldig'));
        return;
      }
      keus.querySelectorAll('.mvb__keuze').forEach((s) => s.classList.remove('is-ongeldig'));
      const stuk = sjabloon.cloneNode(true);
      const waarden = [...keus.querySelectorAll('.mvb__keuze')].map((s) => s.value);
      stuk.querySelectorAll('.mvb__keuze').forEach((s, i) => { s.value = waarden[i]; });
      this.markeer(stuk);
      this.toonBeeld(stuk);
      this.doelStuk.replaceWith(stuk);
      this.doelStuk = null;
      this.venster.close();
      this.controleer(stuk);
    }

    openMaattabel() {
      const knop = document.querySelector('[data-mvm-open]');
      if (knop) knop.click();
    }

    voorVersturen(e) {
      const bar = this.gekozenBar();
      if (!bar) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const stukken = [...bar.querySelectorAll('[data-stuk]')];
      const eigen = this.lijst();
      const ids = stukken.length
        ? stukken.map((s) => this.variantVan(s))
        : [eigen.find((v) => v.beschikbaar) || eigen[0]]; // product zonder varianten
      const fout = ids.some((v, i) => !v || !v.beschikbaar || (stukken[i] && !this.controleer(stukken[i])));
      if (fout) {
        this.toonFout(ONBESCHIKBAAR);
        return;
      }
      this.toonFout('');
      const aantal = Number(bar.dataset.aantal) || 1;
      const sleutel = `${bar.dataset.bar}:${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
      const perVariant = new Map();
      (stukken.length ? ids : Array(aantal).fill(ids[0])).forEach((v) => perVariant.set(v.id, (perVariant.get(v.id) || 0) + 1));

      const los = bar.hasAttribute('data-los');
      const items = [...perVariant].map(([id, q]) => (los ? { id: Number(id), quantity: q } : { id: Number(id), quantity: q, properties: { _mv_bundel: sleutel } }));
      const cadeau = bar.querySelector('[data-cadeau-variant]');
      if (cadeau) items.push({ id: Number(cadeau.dataset.cadeauVariant), quantity: Number(cadeau.dataset.cadeauAantal) || 1, properties: { _mv_cadeau: sleutel } });
      this.voegToe(items);
    }

    // zoals Horizon's eigen batch-toevoeging (product-form.js), maar met regel-eigenschappen
    async voegToe(items) {
      const bron = this.form.closest('product-form-component') || this;
      const knoppen = [...bron.querySelectorAll('button[type="submit"], button[name="add"]')];
      knoppen.forEach((k) => { k.disabled = true; });
      const secties = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
      const uitgesteld = CartLinesUpdateEvent.createPromise();
      bron.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: uitgesteld.promise,
      }));
      try {
        const antwoord = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: secties.join(',') }),
        }).then((r) => r.json());
        const wagen = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`).then((r) => r.json());
        if (antwoord.status) {
          this.toonFout(antwoord.description || antwoord.message || ONBESCHIKBAAR);
          bron.dispatchEvent(new CartErrorEvent({ error: antwoord.message || 'Add to cart failed', code: 'INVALID', detail: { description: antwoord.description } }));
          uitgesteld.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(wagen), detail: { didError: true, items: wagen.items, source: 'mv-bundels' } });
          return;
        }
        uitgesteld.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(wagen),
          detail: { items: wagen.items, source: 'mv-bundels', sourceId: this.id, itemCount: items.reduce((t, i) => t + i.quantity, 0), productId: this.dataset.product, sections: antwoord.sections, didError: false },
        });
      } catch (fout) {
        console.error('[mv-bundels] toevoegen mislukt', fout);
        this.toonFout(ONBESCHIKBAAR);
        uitgesteld.reject(fout);
        bron.dispatchEvent(new CartErrorEvent({ error: fout?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
      } finally {
        knoppen.forEach((k) => { k.disabled = false; });
      }
    }
  }

  customElements.define('mv-bundels', MvBundels);
})();
